import classNames from 'classnames';
import { type ButtonHTMLAttributes, forwardRef, type MouseEvent, type ReactNode, type Ref } from 'react';
import ChevronSm from '../../icons/ChevronSm';
import { Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import styles from './Dropdown.module.scss';

export type DropdownProps = {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  leadingIcon?: IconDefinition<'color' | 'orientation'>;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
  size?: Size<'xs' | 'sm' | 'md'>;
  text: ReactNode;
  title?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  variant?: 'default' | 'secondary' | 'flat';
  width?: 'auto' | 'full';
  'aria-expanded'?: boolean;
  'aria-haspopup'?: 'menu' | 'listbox' | 'dialog' | boolean;
  'aria-controls'?: string;
};

const Dropdown = (
  {
    ariaLabel,
    className,
    disabled,
    leadingIcon,
    onClick,
    size = 'md',
    text,
    title,
    type = 'button',
    variant = 'default',
    width = 'auto',
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHasPopup = 'menu',
    'aria-controls': ariaControls
  }: DropdownProps,
  ref: Ref<HTMLButtonElement>
) => {
  const onButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    }
  };

  const classes = classNames(className, styles.dropdown, styles[size], styles[variant], styles[width], disabled && styles.disabled);

  return (
    <button
      {...setDenaliAttribute('Dropdown')}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      className={classes}
      onClick={onButtonClick}
      disabled={disabled}
      title={title}
      type={type}
      ref={ref}
    >
      <div className={styles.contents}>
        {leadingIcon ? (
          <div className={styles.leadingIcon}>
            <IconRenderer icon={leadingIcon} defaults={{ color: 'currentColor', size: size === 'xs' ? 'xs' : size === 'sm' ? 'sm' : 'md' }} />
          </div>
        ) : null}
        <span className={styles.text}>{text}</span>
      </div>
      <div className={styles.chevron}>
        <ChevronSm color="currentColor" orientation={ariaExpanded ? 'up' : 'down'} size={size} />
      </div>
    </button>
  );
};

export default forwardRef(Dropdown);
